import React, {
  createContext,
  useContext,
  ReactNode,
  useMemo,
  useState,
  useCallback,
  PropsWithChildren,
} from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "react-query";
import {
  Simulator,
  UpsertReleaseParamsDto,
} from "@/v2/entities/simulator/Simulator.model";
import {
  Version,
  getReleasesWithVersions,
} from "@/v2/entities/release/Release.model";
import { TopicReleasePolicy } from "@/v2/entities/topic-release-policy/TopicReleasePolicy.model";
import { useTopicReleasePolicyApi } from "@/v2/entities/topic-release-policy/TopicReleasePolicy.api";
import {
  useGetSimulator,
  useSimulatorApi,
} from "@/v2/entities/simulator/Simulator.api";
import { Topic } from "@/v2/entities/topic/Topic.model";
import { useTopicApi } from "@/v2/entities/topic/Topic.api";
import { useReleaseApi } from "@/v2/entities/release/Release.api";
import { useInstructionApi } from "@/v2/entities/instruction/Instruction.api";
import { usePrerequisiteApi } from "@/v2/entities/prerequisite/Prerequisite.api";
import { UpsertTopicReleasePolicyDto } from "@/v2/entities/topic-release-policy/TopicReleasePolicy.model";
import {
  UpsertReleaseConversationRequest,
  UpsertReleaseConversationResponse,
  UpsertReleaseParamsDto as ReleaseUpsertReleaseParamsDto,
} from "@/v2/entities/release/Release.model";
import { useTrainingSessionEventApi } from "@/v2/entities/training-session-event/TrainingSessionEvent.api";
import { TrainingSessionEventType } from "@/v2/entities/training-session-event/TrainingSessionEvent.model";
import { Skill } from "@/v2/entities/skill/Skill.model";
import { sendWindowPost } from "../parts/chat/chat.types";
import { ChatWrapper } from "./ChatWrapper";
import { InstructionsNavigationProvider } from "./InstructionsNavigationContext";

export interface TemporaryPolicyOverride {
  type: "brief_edit" | "instruction_add" | "prerequisite_add";
  topicId: number;
  content: string;
}

export interface SimulatorWrapperContextType {
  // Simulator data
  simulator: Simulator | null;
  releasesWithVersions: Version[] | null;
  publishedRelease: Version | null;
  topics: Topic[] | null;
  isLoading: boolean;
  isSimulatorLoading: boolean;
  isLoadingTopics: boolean;

  // Release data
  currentRelease: Version | null;
  isLoadingCurrentRelease: boolean;
  currentReleaseError?: unknown;
  topicReleasePolicies: TopicReleasePolicy[] | null;
  currentReleaseSkills: Skill[] | null;
  releaseError: string | null;

  // Temporary policy override
  temporaryPolicyOverride: TemporaryPolicyOverride | null;
  setTemporaryPolicyOverride: (
    override: TemporaryPolicyOverride | null
  ) => void;
  clearTemporaryPolicyOverride: () => void;

  // Description overrides (accumulated across multiple edits)
  descriptionOverrides: Record<number, string>;
  setDescriptionOverride: (topicId: number, content: string) => void;
  removeDescriptionOverride: (topicId: number) => void;
  clearAllDescriptionOverrides: () => void;
  activeDescriptionTopicId: number | null;
  setActiveDescriptionTopicId: (topicId: number | null) => void;

  // Release actions
  refreshReleaseData: () => Promise<void>;
  handleBrief: (brief: string, topicId: number) => Promise<void>;
  updateRelease: (
    releaseId: string,
    release: ReleaseUpsertReleaseParamsDto
  ) => Promise<void>;
  updateTopicReleasePolicy: (
    topicId: number,
    releaseId: number,
    data: UpsertTopicReleasePolicyDto
  ) => Promise<void>;

  createInstruction: (content: string, topicId: number) => Promise<void>;
  updateInstruction: (instructionId: string, content: string) => Promise<void>;
  deleteInstruction: (instructionId: string) => Promise<void>;

  createPrerequisite: (content: string, topicId: number) => Promise<void>;
  updatePrerequisite: (
    prerequisiteId: string,
    content: string
  ) => Promise<void>;
  deletePrerequisite: (prerequisiteId: string) => Promise<void>;

  upsertConversation: (
    data: UpsertReleaseConversationRequest
  ) => Promise<UpsertReleaseConversationResponse>;

  publishRelease: (data: UpsertReleaseParamsDto) => Promise<void>;

  reportTrainingSessionEvent: (
    type: TrainingSessionEventType,
    value: Record<string, unknown>,
    previousValue?: Record<string, unknown>,
    additionalData?: {
      topicId?: number;
      releaseConversationId?: number;
      releaseCommentId?: number;
      topicReleasePolicyId?: string;
      instructionId?: string;
      prerequisiteId?: string;
      ticketInTrainingId?: number;
      conversationInTrainingId?: number;
      ticketId?: string;
      conversationId?: string;
    }
  ) => Promise<void>;
}

export const SimulatorWrapperContext = createContext<
  SimulatorWrapperContextType | undefined
>(undefined);

interface SimulatorWrapperProps {
  simulatorId?: string;
  skillsOverride?: Skill[];
}

export function SimulatorWrapper({
  children,
  simulatorId: propSimulatorId,
  skillsOverride,
}: PropsWithChildren<SimulatorWrapperProps>) {
  const { simulatorId: paramSimulatorId } = useParams();
  const simulatorId = propSimulatorId ?? paramSimulatorId;

  const { getTopics } = useTopicApi();
  const simulatorApi = useSimulatorApi();
  const releaseApi = useReleaseApi();
  const topicReleasePolicyApi = useTopicReleasePolicyApi();
  const instructionApi = useInstructionApi();
  const prerequisiteApi = usePrerequisiteApi();
  const trainingSessionEventApi = useTrainingSessionEventApi();

  const [releaseError, setReleaseError] = useState<string | null>(null);
  const [temporaryPolicyOverride, setTemporaryPolicyOverride] =
    useState<TemporaryPolicyOverride | null>(null);

  const clearTemporaryPolicyOverride = useCallback(() => {
    setTemporaryPolicyOverride(null);
  }, []);

  const [descriptionOverrides, setDescriptionOverrides] = useState<
    Record<number, string>
  >({});
  const [activeDescriptionTopicId, setActiveDescriptionTopicId] = useState<
    number | null
  >(null);

  const setDescriptionOverride = useCallback(
    (topicId: number, content: string) => {
      setDescriptionOverrides((prev) => ({ ...prev, [topicId]: content }));
    },
    []
  );

  const removeDescriptionOverride = useCallback((topicId: number) => {
    setDescriptionOverrides((prev) => {
      const next = { ...prev };
      delete next[topicId];
      return next;
    });
  }, []);

  const clearAllDescriptionOverrides = useCallback(() => {
    setDescriptionOverrides({});
  }, []);

  const topics = useQuery({
    queryKey: ["topics"],
    queryFn: () => getTopics(),
    enabled: true,
  });

  if (!simulatorId) {
    throw new Error("Simulator ID is required");
  }

  const simulatorQuery = useGetSimulator(simulatorId);

  const { publishedReleaseId, draftReleaseId } = simulatorQuery?.data ?? {};

  const currentReleaseQuery = useQuery({
    queryKey: ["release", draftReleaseId],
    queryFn: () =>
      releaseApi.getRelease(draftReleaseId?.toString() ?? "", true, true),
    enabled: !!draftReleaseId,
  });

  const currentRelease = useMemo(() => {
    return currentReleaseQuery.data ?? null;
  }, [currentReleaseQuery.data]);

  const currentReleaseSkillsQuery = useQuery({
    queryKey: ["release", draftReleaseId, "skills"],
    queryFn: () =>
      releaseApi.getReleaseSkills(draftReleaseId?.toString() || ""),
    enabled: !!draftReleaseId,
  });

  const currentReleaseSkills = useMemo(() => {
    return currentReleaseSkillsQuery.data?.skills ?? null;
  }, [currentReleaseSkillsQuery.data]);

  const publishedReleaseQuery = useQuery({
    queryKey: ["release", publishedReleaseId],
    queryFn: () =>
      releaseApi.getRelease(publishedReleaseId?.toString() ?? "", true, true),
    enabled: !!publishedReleaseId,
  });

  const publishedRelease = useMemo(() => {
    return publishedReleaseQuery.data ?? null;
  }, [publishedReleaseQuery.data]);

  const releasesQuery = useQuery({
    queryKey: ["simulator-releases", simulatorId],
    queryFn: () =>
      simulatorApi.getSimulatorReleases(simulatorId, false, false, true),
    enabled: !!simulatorId,
    select: (data) => getReleasesWithVersions(data),
  });

  const releasesWithVersions = useMemo(() => {
    return releasesQuery.data ?? null;
  }, [releasesQuery.data]);

  const refreshReleaseData = useCallback(async () => {
    await Promise.all([
      currentReleaseQuery.refetch(),
      publishedReleaseQuery.refetch(),
      releasesQuery.refetch(),
    ]);
  }, [
    currentReleaseQuery.refetch,
    publishedReleaseQuery.refetch,
    releasesQuery.refetch,
  ]);

  const refreshSimulatorData = useCallback(async () => {
    await simulatorQuery.refetch();
    await refreshReleaseData();
  }, [simulatorQuery, refreshReleaseData]);

  const getTopicCurrentReleasePolicyId = useCallback(
    async (topicId: number): Promise<string | null> => {
      if (!currentRelease?.id) {
        setReleaseError("Something is wrong, releaseId is not set");
        return null;
      }

      let topicReleasePolicyId =
        currentRelease.topicReleasePolicies?.find(
          (policy) => policy.topicId === topicId
        )?.id ?? null;

      if (!topicReleasePolicyId) {
        try {
          const newTopicReleasePolicy =
            await topicReleasePolicyApi.upsertTopicReleasePolicy({
              topicId,
              releaseId: currentRelease.id,
              brief: "",
            });
          topicReleasePolicyId = newTopicReleasePolicy.id;
        } catch (err) {
          setReleaseError(
            err instanceof Error
              ? err.message
              : "Failed to upsert topic release policy"
          );
          return null;
        }
      }

      return topicReleasePolicyId;
    },
    [currentRelease, currentRelease?.id, topicReleasePolicyApi, setReleaseError]
  );

  const createInstruction = useCallback(
    async (content: string, topicId: number): Promise<void> => {
      try {
        setReleaseError(null);

        const topicReleasePolicyId = await getTopicCurrentReleasePolicyId(
          topicId
        );

        if (!topicReleasePolicyId) {
          setReleaseError(
            "Something is wrong, topic release policy id is not set"
          );
          return;
        }

        await instructionApi.createInstruction({
          content,
          topicReleasePolicyId,
        });
        await refreshReleaseData();
        reloadMessage();
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to create instruction";
        setReleaseError(errorMessage);
        throw new Error(errorMessage);
      }
    },
    [instructionApi, refreshReleaseData, getTopicCurrentReleasePolicyId]
  );

  const updateInstruction = useCallback(
    async (instructionId: string, content: string): Promise<void> => {
      try {
        setReleaseError(null);
        await instructionApi.updateInstruction(instructionId, { content });
        await refreshReleaseData();
        reloadMessage();
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to update instruction";
        setReleaseError(errorMessage);
        throw new Error(errorMessage);
      }
    },
    [instructionApi, refreshReleaseData]
  );

  const deleteInstruction = useCallback(
    async (instructionId: string): Promise<void> => {
      try {
        setReleaseError(null);
        await instructionApi.deleteInstruction(instructionId);
        await refreshReleaseData();
        reloadMessage();
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to delete instruction";
        setReleaseError(errorMessage);
        throw new Error(errorMessage);
      }
    },
    [instructionApi, refreshReleaseData]
  );

  const createPrerequisite = useCallback(
    async (content: string, topicId: number): Promise<void> => {
      try {
        setReleaseError(null);

        const topicReleasePolicyId = await getTopicCurrentReleasePolicyId(
          topicId
        );

        if (!topicReleasePolicyId) {
          setReleaseError(
            "Something is wrong, topic release policy id is not set"
          );
          return;
        }

        await prerequisiteApi.createPrerequisite({
          content,
          topicReleasePolicyId,
        });
        await refreshReleaseData();
        reloadMessage();
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to create prerequisite";
        setReleaseError(errorMessage);
        throw new Error(errorMessage);
      }
    },
    [prerequisiteApi, refreshReleaseData, getTopicCurrentReleasePolicyId]
  );

  const updatePrerequisite = useCallback(
    async (prerequisiteId: string, content: string): Promise<void> => {
      try {
        setReleaseError(null);
        await prerequisiteApi.updatePrerequisite(prerequisiteId, { content });
        await refreshReleaseData();
        reloadMessage();
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to update prerequisite";
        setReleaseError(errorMessage);
        throw new Error(errorMessage);
      }
    },
    [prerequisiteApi, refreshReleaseData]
  );

  const deletePrerequisite = useCallback(
    async (prerequisiteId: string): Promise<void> => {
      try {
        setReleaseError(null);
        await prerequisiteApi.deletePrerequisite(prerequisiteId);
        await refreshReleaseData();
        reloadMessage();
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to delete prerequisite";
        setReleaseError(errorMessage);
        throw new Error(errorMessage);
      }
    },
    [prerequisiteApi, refreshReleaseData]
  );

  const upsertConversation = useCallback(
    async (
      data: UpsertReleaseConversationRequest
    ): Promise<UpsertReleaseConversationResponse> => {
      try {
        setReleaseError(null);
        const response = await releaseApi.upsertConversation(data);
        return response;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to upsert conversation";
        setReleaseError(errorMessage);
        throw new Error(errorMessage);
      }
    },
    [releaseApi]
  );

  const handleBrief = useCallback(
    async (brief: string, topicId: number) => {
      if (!currentRelease?.id) return;

      await topicReleasePolicyApi.updateTopicReleasePolicy(
        topicId,
        currentRelease.id,
        { brief, topicId, releaseId: currentRelease.id }
      );
      await refreshReleaseData();
      reloadMessage();
    },
    [topicReleasePolicyApi, currentRelease?.id]
  );

  const updateRelease = useCallback(
    async (releaseId: string, release: ReleaseUpsertReleaseParamsDto) => {
      await releaseApi.updateRelease(releaseId, release);
      await refreshReleaseData();
    },
    [releaseApi, refreshReleaseData]
  );

  const updateTopicReleasePolicy = useCallback(
    async (
      topicId: number,
      releaseId: number,
      data: UpsertTopicReleasePolicyDto
    ) => {
      await topicReleasePolicyApi.updateTopicReleasePolicy(
        topicId,
        releaseId,
        data
      );
      await refreshReleaseData();
    },
    [topicReleasePolicyApi, refreshReleaseData]
  );

  const publishRelease = useCallback(
    async ({
      releasePolicy,
      filterPolicy,
      customToneOfVoice,
    }: UpsertReleaseParamsDto) => {
      if (!currentRelease?.id) return;

      await simulatorApi.publishRelease(
        simulatorId,
        currentRelease.id.toString(),
        { releasePolicy, filterPolicy, customToneOfVoice }
      );
      await refreshSimulatorData();
      await refreshReleaseData();
    },
    [currentRelease, simulatorApi, simulatorId, refreshSimulatorData]
  );

  const reloadMessage = useCallback(async () => {
    sendWindowPost({
      type: "reload",
    });
  }, []);

  const reportTrainingSessionEvent = useCallback(
    async (
      type: TrainingSessionEventType,
      value: Record<string, unknown>,
      previousValue?: Record<string, unknown>,
      additionalData?: {
        topicId?: number;
        releaseConversationId?: number;
        releaseCommentId?: number;
        topicReleasePolicyId?: string;
        instructionId?: string;
        prerequisiteId?: string;
        ticketInTrainingId?: number;
        conversationInTrainingId?: number;
        ticketId?: string;
        conversationId?: string;
      }
    ): Promise<void> => {
      if (!simulatorId || !currentRelease?.id) {
        console.warn(
          "Cannot report training session event: missing simulatorId or releaseId"
        );
        return;
      }

      try {
        await trainingSessionEventApi.createEvent({
          type,
          value,
          previousValue,
          simulatorId: parseInt(simulatorId),
          releaseId: currentRelease.id,
          ...additionalData,
        });
      } catch (error) {
        console.error("Failed to report training session event:", error);
      }
    },
    [simulatorId, currentRelease?.id, trainingSessionEventApi]
  );

  const isLoading = useMemo(() => {
    // Only wait for simulator to load, not the release data
    if (
      (simulatorQuery.isLoading && !simulatorQuery.data) ||
      (currentReleaseQuery.isLoading && !currentReleaseQuery.data)
    ) {
      return true;
    }
    return false;
  }, [simulatorQuery.isLoading, currentReleaseQuery.isLoading]);

  const value: SimulatorWrapperContextType = {
    // Simulator data
    simulator: simulatorQuery.data ?? null,
    releasesWithVersions,
    publishedRelease,
    topics: topics.data ?? null,
    isSimulatorLoading: simulatorQuery.isLoading || topics.isLoading,
    isLoadingTopics: topics.isLoading,
    isLoading,

    // Release data
    currentRelease,
    isLoadingCurrentRelease: currentReleaseQuery.isLoading,
    currentReleaseError: currentReleaseQuery.error,
    topicReleasePolicies: currentRelease?.topicReleasePolicies ?? null,
    currentReleaseSkills,
    releaseError,

    // Temporary policy override
    temporaryPolicyOverride,
    setTemporaryPolicyOverride,
    clearTemporaryPolicyOverride,

    // Description overrides
    descriptionOverrides,
    setDescriptionOverride,
    removeDescriptionOverride,
    clearAllDescriptionOverrides,
    activeDescriptionTopicId,
    setActiveDescriptionTopicId,

    // Release actions
    refreshReleaseData,
    handleBrief,
    updateRelease,
    updateTopicReleasePolicy,
    createInstruction,
    updateInstruction,
    deleteInstruction,
    createPrerequisite,
    updatePrerequisite,
    deletePrerequisite,
    upsertConversation,
    publishRelease,
    reportTrainingSessionEvent,
  };

  return (
    <SimulatorWrapperContext.Provider value={value}>
      <InstructionsNavigationProvider>
        <ChatWrapper skillsOverride={skillsOverride}>{children}</ChatWrapper>
      </InstructionsNavigationProvider>
    </SimulatorWrapperContext.Provider>
  );
}

export function useSimulator(): SimulatorWrapperContextType {
  const context = useContext(SimulatorWrapperContext);
  if (context === undefined) {
    throw new Error("useSimulator must be used within a SimulatorWrapper");
  }
  return context;
}

export function useSimulatorOptional(): SimulatorWrapperContextType | null {
  return useContext(SimulatorWrapperContext) ?? null;
}
